import VisibilityIcon from "@mui/icons-material/Visibility";
import { Button, IconButton, SxProps, Theme } from "@mui/material";
import * as React from "react";

import { useAuth, useSystemUser } from "@/providers/auth/hooks";
import { OrgRecord } from "@/shared/domain/orgs";
import { useOrg } from "@/shared/domain/orgs/hooks";

import { RobotoTooltip } from "./RobotoTooltip";
import { SystemUserOnly } from "./SystemUserOnly";

type ViewAsOrgButtonProps = {
  /**
   * Organization ID to view as. If provided, the component will fetch the org data.
   * Either orgId or org must be provided, but not both.
   */
  orgId?: string;
  /**
   * Organization record to view as. If provided, orgId will be ignored.
   * Either orgId or org must be provided, but not both.
   */
  org?: OrgRecord;
  /**
   * Optional variant for the button. Defaults to "outlined".
   * Use "icon" for an icon-only button.
   */
  variant?: "text" | "outlined" | "contained" | "icon";
  /**
   * Optional size for the button. Defaults to "medium".
   */
  size?: "small" | "medium" | "large";
  /**
   * Optional sx prop for styling the button.
   */
  sx?: SxProps<Theme>;
};

/**
 * A button component that allows system users to view as a specific organization.
 * The button is automatically wrapped in SystemUserOnly, so it will only be visible
 * to system users.
 */
export const ViewAsOrgButton: React.FC<ViewAsOrgButtonProps> = ({
  orgId,
  org,
  variant = "outlined",
  size = "medium",
  sx,
}) => {
  const { setCurrentOrganization } = useAuth();
  const { isSystemUser } = useSystemUser();

  // Don't make a request if:
  // 1. We're not a system user (because it'd always 403)
  // 2. We already have the org data (because it's unnecessary)
  const orgQuery = useOrg(isSystemUser && !org ? orgId : undefined);
  const targetOrg = org || orgQuery.data;

  // Don't render if we don't have any org data
  if (!targetOrg && !orgId) {
    return null;
  }

  const handleClick = () => {
    if (!targetOrg) {
      return;
    }

    setCurrentOrganization(targetOrg);
  };

  const tooltipTitle = targetOrg
    ? `View as ${targetOrg.name} (${targetOrg.org_id})`
    : "Loading...";

  const buttonComponent =
    variant === "icon" ? (
      <IconButton onClick={handleClick} size={size} sx={sx}>
        <VisibilityIcon fontSize="small" />
      </IconButton>
    ) : (
      <Button
        variant={variant}
        disableElevation
        disabled={!targetOrg}
        onClick={handleClick}
        size={size}
        sx={{
          textTransform: "none",
          ...sx,
        }}
        startIcon={<VisibilityIcon fontSize="small" />}
      >
        View As
      </Button>
    );

  return (
    <SystemUserOnly>
      <RobotoTooltip title={tooltipTitle}>
        <span>{buttonComponent}</span>
      </RobotoTooltip>
    </SystemUserOnly>
  );
};
